import { Globe, Mail, Phone, QrCode, MessageSquareText } from "lucide-react";
import { ORG } from "@/data/tools";
import { scrollToId } from "@/lib/scroll";
import { Reveal, LogoMark } from "@/components/Reveal";

const AUDIENCE = ["SENDCOs", "Inclusion Leads", "Headteachers", "School Leaders", "Governors"];

export const PrintCover = () => (
  <div className="print-cover">
    <LogoMark className="h-24 w-24" />
    <p className="mt-10 text-lg font-bold tracking-[0.2em]">THE SEND ACCOUNTABILITY PROJECT</p>
    <p className="mt-2 font-bold tracking-[0.2em]">FREE SENDCO RESOURCE PACK 2026</p>
    <h1 className="mt-8 font-display text-6xl font-medium leading-[1]">SENDCO<br />Resource Pack</h1>
    <p className="mt-6 text-xl">Practical tools, audits and templates for:</p>
    <ul className="mt-2 text-lg">{AUDIENCE.map((a) => <li key={a}>• {a}</li>)}</ul>
    <p className="mt-10 text-lg"><strong>{ORG.name}</strong><br />Supporting schools, families and professionals through practical SEND resources, toolkits and insights.</p>
  </div>
);

export const PageFooterText = () => (
  <p className="text-xs leading-snug print:text-[10px]">
    {ORG.name} · Supporting schools, families and professionals through practical SEND resources, toolkits and insights. · {ORG.web} · Company No. {ORG.company} | ICO Registration: {ORG.ico}
  </p>
);

const Block = ({ title, children }) => (
  <div>
    <h4 className="font-display text-xl font-medium">{title}</h4>
    <div className="mt-2 leading-relaxed text-ink-soft">{children}</div>
  </div>
);

export const Welcome = () => (
  <Reveal as="section" id="welcome" aria-labelledby="welcome-title" data-testid="welcome-section"
    className="tool-section scroll-mt-36 rounded-[1.75rem] border-2 border-line bg-paper p-5 sm:p-8 lg:p-10">
    <p className="eyebrow">Welcome</p>
    <h3 id="welcome-title" className="mt-3 font-display text-3xl font-medium leading-tight sm:text-4xl">Why this pack exists.</h3>
    <p className="mt-5 max-w-3xl text-lg leading-relaxed">
      Founded by a parent of three children with SEND, The SEND Accountability Project was created to support schools, families and professionals through practical resources that improve outcomes for children and young people.
    </p>
    <p className="mt-3 text-ink-soft">Practical tools, audits and templates for {AUDIENCE.join(", ").replace(/, (?=[^,]*$)/, " and ")}.</p>
    <div className="mt-10 grid gap-8 md:grid-cols-3">
      <Block title="About The SEND Accountability Project">
        <p data-testid="welcome-about">The SEND Accountability Project exists to support improved outcomes for children and young people with SEND through practical resources, accessible tools and collaborative approaches.</p>
      </Block>
      <Block title="What schools can expect">
        <p>Free, practical tools you can use straight away: a health check, a provision audit, review templates, governor questions, a workload audit and a development plan.</p>
      </Block>
      <Block title="How to use the pack">
        <p>Fill in any section on screen. Your answers save in this browser only. Then print a section, print the whole pack with your answers, or download the blank PDF.</p>
      </Block>
    </div>
    <div className="mt-10 grid gap-8 lg:grid-cols-[minmax(0,1.3fr)_minmax(0,0.7fr)]">
      <div>
        <h4 className="font-display text-xl font-medium">How to access</h4>
        <p className="mt-2 leading-relaxed text-ink-soft" data-testid="welcome-access">
          Fill in any section on screen, download the blank PDF to complete by hand, or print your answers. Links to our partner sites are in{" "}
          <a href="#resource-directory" onClick={(e) => { e.preventDefault(); scrollToId("resource-directory"); }} className="font-bold text-brand underline-offset-4 hover:underline">section 09, Resource Directory</a>.
        </p>
        <div className="mt-6 rounded-2xl border-2 border-line bg-surface p-5">
          <p className="flex items-center gap-2 font-bold"><MessageSquareText size={18} aria-hidden /> We would love your feedback</p>
          <p className="mt-1 text-ink-soft">Tell us what helped and what we should build next.</p>
          <a href="#feedback" onClick={(e) => { e.preventDefault(); scrollToId("feedback"); }} className="btn btn-brand btn-sm no-print mt-4" data-testid="welcome-feedback">Give feedback</a>
        </div>
      </div>
      <div className="space-y-4">
        <ul className="space-y-3 text-sm">
          <li className="flex gap-2.5"><Globe size={18} className="shrink-0" aria-hidden /><a href={`https://${ORG.web}`} target="_blank" rel="noopener noreferrer" className="break-all font-bold">{ORG.web}</a></li>
          <li className="flex gap-2.5"><Mail size={18} className="shrink-0" aria-hidden /><a href={`mailto:${ORG.director}`} className="break-all font-bold">{ORG.director}</a></li>
          <li className="flex gap-2.5"><Phone size={18} className="shrink-0" aria-hidden /><a href={`tel:${ORG.phone.replace(/\s/g, "")}`} className="font-bold">{ORG.phone}</a></li>
        </ul>
        <div className="flex aspect-square max-w-[170px] flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-ink-soft text-center text-sm text-ink-soft" data-testid="welcome-qr-placeholder">
          <QrCode size={40} aria-hidden />
          QR code<br />(placeholder)
        </div>
      </div>
    </div>
  </Reveal>
);
